import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import Footer from './Footer';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import baseurl from '../Baseurl/baseurl';
import { Card, FormField, Input, Textarea, Button, Eyebrow, Loader, Container } from '../ui';

const FamilyDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    father_name: '',
    father_contact: '',
    mother_name: '',
    mother_contact: '',
    spouse_name: '',
    spouse_contact: '',
    number_of_children: '',
    children_names: '',
    family_address: '',
    marital_status: ''
  });

  useEffect(() => {
    const fetchMemberData = async () => {
      try {
        setFetchLoading(true);
        const token = localStorage.getItem('token');

        if (!token) {
          setError('Authentication required');
          return;
        }

        const response = await fetch(`${baseurl}/api/member/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.data) {
          const memberData = data.data;

          localStorage.setItem('memberId', memberData.mid);

          setFormData(prev => ({
            ...prev,
            father_name: memberData.MemberFamily?.father_name || '',
            father_contact: memberData.MemberFamily?.father_contact || '',
            mother_name: memberData.MemberFamily?.mother_name || '',
            mother_contact: memberData.MemberFamily?.mother_contact || '',
            spouse_name: memberData.MemberFamily?.spouse_name || '',
            spouse_contact: memberData.MemberFamily?.spouse_contact || '',
            number_of_children: memberData.MemberFamily?.number_of_children || '',
            children_names: memberData.MemberFamily?.children_names
              ? JSON.parse(memberData.MemberFamily.children_names).join(', ')
              : '',
            family_address: memberData.MemberFamily?.address || '',
            marital_status: memberData.marital_status || ''
          }));
        } else {
          setError(data.message || 'Failed to load member data');
        }
      } catch (err) {
        console.error('Error fetching member data:', err);
        setError(`Failed to load member data: ${err.message}`);
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchMemberData();
    } else {
      setFetchLoading(false);
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const memberId = localStorage.getItem('memberId');

      if (!token) {
        setError('Authentication required');
        return;
      }

      if (!memberId) {
        setError('Member ID not found');
        return;
      }

      const familyDataToSend = {
        member_id: parseInt(memberId),
        father_name: formData.father_name,
        father_contact: formData.father_contact,
        mother_name: formData.mother_name,
        mother_contact: formData.mother_contact,
        spouse_name: formData.spouse_name,
        spouse_contact: formData.spouse_contact,
        number_of_children: parseInt(formData.number_of_children) || 0,
        children_names: formData.children_names
          ? formData.children_names.split(',').map(name => name.trim())
          : [],
        address: formData.family_address,
        marital_status: formData.marital_status
      };

      const response = await fetch(`${baseurl}/api/family-details/update/${memberId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(familyDataToSend)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success !== false) {
        setSuccess('Family details updated successfully');
        setTimeout(() => navigate(-1), 2000);
      } else {
        setError(data.message || 'Failed to update family details');
      }
    } catch (err) {
      console.error('Error updating family details:', err);
      setError(`Failed to update family details: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <Loader label="Loading family details…" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg pb-24">
      <Header />
      {/* Dark hero band with back action */}
      <div className="on-dark bg-navy">
        <Container className="flex items-center gap-3 py-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full ring-1 ring-inset ring-line/20 text-ink transition-all duration-300 hover:bg-primary/10 hover:ring-primary/40"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <Eyebrow>Family</Eyebrow>
            <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
              {t('familyInfo')}
            </h1>
          </div>
        </Container>
      </div>

      <Container width="narrow" className="py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 rounded-md border border-danger/20 bg-danger/10 px-4 py-3 text-sm font-medium text-danger">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 rounded-md border border-success/20 bg-success/10 px-4 py-3 text-sm font-medium text-success">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {success}
            </div>
          )}

          <Card padding="lg" className="space-y-5">
            <div>
              <Eyebrow>Family</Eyebrow>
              <h2 className="mt-1 font-display text-xl font-bold tracking-tight text-ink">
                {t('Family')} <em className="kbc-serif">Details</em>
              </h2>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <FormField label={t('fathername')} htmlFor="father_name">
                <Input
                  id="father_name"
                  name="father_name"
                  value={formData.father_name}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField label={t('fathercontact')} htmlFor="father_contact">
                <Input
                  id="father_contact"
                  name="father_contact"
                  value={formData.father_contact}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField label={t('mothername')} htmlFor="mother_name">
                <Input
                  id="mother_name"
                  name="mother_name"
                  value={formData.mother_name}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField label={t('mothercontact')} htmlFor="mother_contact">
                <Input
                  id="mother_contact"
                  name="mother_contact"
                  value={formData.mother_contact}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField label={t('spousename')} htmlFor="spouse_name">
                <Input
                  id="spouse_name"
                  name="spouse_name"
                  value={formData.spouse_name}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField label={t('spousecontact')} htmlFor="spouse_contact">
                <Input
                  id="spouse_contact"
                  name="spouse_contact"
                  value={formData.spouse_contact}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField label={t('numberofchildren')} htmlFor="number_of_children">
                <Input
                  id="number_of_children"
                  name="number_of_children"
                  type="number"
                  value={formData.number_of_children}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField label={t('maritalstatus')} htmlFor="marital_status">
                <Input
                  id="marital_status"
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleInputChange}
                />
              </FormField>
            </div>

            <FormField
              label={t('childrennames')}
              htmlFor="children_names"
              hint="Separate names by comma (e.g. Anu, Arjun)"
            >
              <Input
                id="children_names"
                name="children_names"
                value={formData.children_names}
                onChange={handleInputChange}
              />
            </FormField>

            <FormField label={t('familyaddress')} htmlFor="family_address">
              <Textarea
                id="family_address"
                name="family_address"
                value={formData.family_address}
                onChange={handleInputChange}
                rows={3}
              />
            </FormField>
          </Card>

          <Button type="submit" loading={loading} disabled={loading} fullWidth size="lg">
            {loading ? 'Saving…' : t('updateChanges')}
          </Button>
        </form>
      </Container>

      <Footer />
    </div>
  );
};

export default FamilyDetailsPage;
